import React from 'react';
import styled from 'styled-components';
import { Fade, Slide } from 'react-awesome-reveal';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle } from '@/components/common';
import { formatRelativeTime } from '@/utils';
import { useDashboard, formatAmount } from '@/api/dashboard';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.lg};
  position: relative;
  overflow: hidden;
  
  /* Gradient accent line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ theme }) => theme.colors.primaryGradient};
  }
  
  /* Subtle glow on hover */
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: linear-gradient(135deg, ${({ $color }) => $color}20 0%, ${({ $color }) => $color}10 100%);
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  
  /* Gradient border effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px;
    background: linear-gradient(135deg, ${({ $color }) => $color}40 0%, transparent 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const StatChange = styled.div<{ $positive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-top: ${({ theme }) => theme.spacing.sm};
  background: ${({ $positive, theme }) => $positive ? theme.colors.successLight : theme.colors.errorLight};
  color: ${({ $positive, theme }) => $positive ? theme.colors.success : theme.colors.error};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

// Activity List
const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div<{ $type: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  ${({ $type, theme }) => {
    switch ($type) {
      case 'payment':
        return `background: ${theme.colors.successLight}; color: ${theme.colors.success};`;
      case 'enrollment':
        return `background: ${theme.colors.primaryLight}; color: ${theme.colors.primary};`;
      case 'booking':
        return `background: ${theme.colors.infoLight}; color: ${theme.colors.info};`;
      case 'lead':
        return `background: ${theme.colors.warningLight}; color: ${theme.colors.warning};`;
      default:
        return `background: ${theme.colors.backgroundTertiary}; color: ${theme.colors.textSecondary};`;
    }
  }}
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ActivityDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ActivityTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

// Upcoming Classes
const ClassList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClassItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ClassTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.primary};
  min-width: 80px;
`;

const ClassInfo = styled.div`
  flex: 1;
`;

const ClassSubject = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ClassDetails = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const Dashboard: React.FC = () => {
  const { data, isLoading } = useDashboard();

  const stats = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
      ),
      color: '#FF6B35',
      label: 'Active Students',
      value: isLoading ? '—' : (data?.active_students ?? 0),
      change: '',
      positive: true,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
      color: '#22C55E',
      label: 'Sessions This Week',
      value: isLoading ? '—' : (data?.sessions_this_week ?? 0),
      change: '',
      positive: true,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      color: '#FFB347',
      label: 'Payments This Month',
      value: isLoading ? '—' : formatAmount(data?.payments_this_month_total ?? 0),
      change: '',
      positive: true,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      color: '#FF8C42',
      label: 'Sessions Today',
      value: isLoading ? '—' : (data?.sessions_today ?? 0),
      change: '',
      positive: true,
    },
  ];

  return (
    <PageContainer
      title="Dashboard"
      subtitle="Welcome back! Here's what's happening at Candeza today."
    >
      <Fade triggerOnce cascade damping={0.1}>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index} padding="md">
              <StatIcon $color={stat.color}>{stat.icon}</StatIcon>
              <StatContent>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
                {stat.change && <StatChange $positive={stat.positive}>{stat.change}</StatChange>}
              </StatContent>
            </StatCard>
          ))}
        </StatsGrid>
      </Fade>

      <ContentGrid>
        <MainColumn>
          <Slide triggerOnce direction="up">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Recent Payments</CardTitle>
              </CardHeader>
              <ActivityList>
                {isLoading && <p style={{ color: '#64748b', padding: '8px 0' }}>Loading…</p>}
                {!isLoading && (data?.recent_payments ?? []).length === 0 && (
                  <p style={{ color: '#64748b', padding: '8px 0' }}>No recent payments.</p>
                )}
                {(data?.recent_payments ?? []).map((p) => (
                  <ActivityItem key={p.payment_id}>
                    <ActivityIcon $type="payment">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                    </ActivityIcon>
                    <ActivityContent>
                      <ActivityTitle>{p.student_name ?? 'Unknown student'}</ActivityTitle>
                      <ActivityDescription>
                        {formatAmount(p.amount)} — {p.invoice_number ?? 'payment'}
                      </ActivityDescription>
                    </ActivityContent>
                    <ActivityTime>{formatRelativeTime(p.paid_at)}</ActivityTime>
                  </ActivityItem>
                ))}
              </ActivityList>
            </Card>
          </Slide>
        </MainColumn>

        <SideColumn>
          <Slide triggerOnce direction="right">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Upcoming Sessions</CardTitle>
              </CardHeader>
              <ClassList>
                {isLoading && <p style={{ color: '#64748b', padding: '8px 0' }}>Loading…</p>}
                {!isLoading && (data?.upcoming_sessions ?? []).length === 0 && (
                  <p style={{ color: '#64748b', padding: '8px 0' }}>No upcoming sessions.</p>
                )}
                {(data?.upcoming_sessions ?? []).map((s) => (
                  <ClassItem key={s.session_id}>
                    <ClassTime>{new Date(s.starts_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</ClassTime>
                    <ClassInfo>
                      <ClassSubject>{s.subject_name}</ClassSubject>
                      <ClassDetails>{s.class_name}{s.teacher_name ? ` • ${s.teacher_name}` : ''}</ClassDetails>
                    </ClassInfo>
                  </ClassItem>
                ))}
              </ClassList>
            </Card>
          </Slide>
        </SideColumn>
      </ContentGrid>
    </PageContainer>
  );
};

